import React, { useState } from 'react';
import { useConsultation } from '../contexts/ConsultationContext';
import DrawerShell from './DrawerShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { CalendarIcon, BedDouble } from 'lucide-react';
import { format } from 'date-fns';

export function AdmissionRequestModal({ open, onOpenChange }) {
  const { saveDraft } = useConsultation();
  const [admissionDate, setAdmissionDate] = useState(null);
  const [consultant, setConsultant] = useState('');
  const [ward, setWard] = useState('');
  const [remarks, setRemarks] = useState('');

  const handleSubmit = () => {
    const admissionData = {
      admission_date: admissionDate ? format(admissionDate, 'yyyy-MM-dd') : null,
      consultant,
      ward,
      remarks,
      requested_at: new Date().toISOString(),
    };
    saveDraft('admission_request', admissionData);
    onOpenChange(false);
    setAdmissionDate(null);
    setConsultant('');
    setWard('');
    setRemarks('');
  };

  return (
    <DrawerShell
      open={open}
      onClose={() => onOpenChange(false)}
      eyebrow="Inpatient Workflow"
      title="Admission Request"
      subtitle="Capture admit details and notify the inpatient desk"
      accent="#0369a1"
      icon={<BedDouble className="h-5 w-5" />}
      width="md"
      testId="admission-modal"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="admission-cancel">
            Cancel
          </Button>
          <Button onClick={handleSubmit} className="bg-blue-700 hover:bg-blue-800" data-testid="admission-submit">
            Submit Request
          </Button>
        </>
      }
    >
      <div className="p-5 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="admission-date">Admission Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal"
                data-testid="admission-date-btn"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {admissionDate ? format(admissionDate, 'PPP') : 'Select date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={admissionDate}
                onSelect={setAdmissionDate}
                initialFocus
                data-testid="admission-calendar"
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label htmlFor="consultant">Admitting Consultant</Label>
          <Input
            id="consultant"
            value={consultant}
            onChange={(e) => setConsultant(e.target.value)}
            placeholder="Dr. Name / Specialty"
            data-testid="admission-consultant"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="ward">Preferred Ward</Label>
          <Input
            id="ward"
            value={ward}
            onChange={(e) => setWard(e.target.value)}
            placeholder="General / ICU / Private"
            data-testid="admission-ward"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="remarks">Remarks / Reason for Admission</Label>
          <Textarea
            id="remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Enter reason for admission and any special instructions..."
            rows={4}
            data-testid="admission-remarks"
          />
        </div>
      </div>
    </DrawerShell>
  );
}
